import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Trash2, Download, Search, Filter, History, X, ChevronRight } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import ImageUploader from '../components/ImageUploader';
import type { MedaImage } from '../types/image';

type FilterMode = 'all' | 'favorites' | 'uploads' | 'generated' | 'edited';

export default function LibraryPage() {
  const { images, addImage, removeImage, toggleFavorite } = useLibrary();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [historyFor, setHistoryFor] = useState<MedaImage | null>(null);

  const ancestors = useMemo(() => {
    if (!historyFor) return [];
    const chain: MedaImage[] = [];
    let cursor: MedaImage | undefined = historyFor;
    const seen = new Set<string>();
    while (cursor?.parentId && !seen.has(cursor.parentId)) {
      seen.add(cursor.parentId);
      const parent = images.find(i => i.id === cursor!.parentId);
      if (!parent) break;
      chain.push(parent);
      cursor = parent;
    }
    return chain.reverse(); // oldest-first
  }, [historyFor, images]);

  const children = useMemo(() => {
    if (!historyFor) return [];
    return images.filter(i => i.parentId === historyFor.id);
  }, [historyFor, images]);

  const filtered = useMemo(() => {
    let result = images;
    if (filter === 'favorites') result = result.filter(i => i.favorite);
    else if (filter !== 'all') result = result.filter(i => i.source === filter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i => i.name.toLowerCase().includes(q) || i.tags.some(t => t.toLowerCase().includes(q)));
    }
    return result;
  }, [images, filter, search]);

  const handleUpload = useCallback(async (files: File[]) => {
    for (const file of files) {
      await addImage(file, file.name);
    }
  }, [addImage]);

  const handleDownload = useCallback((img: { blob: Blob; name: string }) => {
    const url = URL.createObjectURL(img.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = img.name;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const filters: { key: FilterMode; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'favorites', label: 'Favorites' },
    { key: 'uploads', label: 'Uploads' },
    { key: 'generated', label: 'Generated' },
    { key: 'edited', label: 'Edited' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 sage-animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold" style={{ fontFamily: 'var(--sage-font-serif)' }}>Library</h1>
          <p className="text-xs mt-1" style={{ color: 'var(--sage-text-muted)' }}>{images.length} images</p>
        </div>
        <ImageUploader onUpload={handleUpload} compact />
      </div>

      {/* Search and filter */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 max-w-sm"
          style={{ backgroundColor: 'var(--sage-surface)', border: '1px solid var(--sage-white-10)' }}
        >
          <Search size={14} style={{ color: 'var(--sage-text-muted)' }} />
          <input
            type="text"
            placeholder="Search images..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-sm flex-1"
            style={{ color: 'var(--sage-text)' }}
          />
        </div>
        <div className="flex items-center gap-1">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className="px-3 py-1.5 rounded-lg text-xs transition-colors"
              style={{
                backgroundColor: filter === f.key ? 'var(--sage-surface)' : 'transparent',
                color: filter === f.key ? 'var(--sage-text)' : 'var(--sage-text-muted)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Image grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
            {images.length === 0 ? 'No images yet. Upload one to get started.' : 'No images match your filter.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filtered.map(img => (
            <div
              key={img.id}
              className="group relative rounded-xl overflow-hidden transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: 'var(--sage-surface)' }}
            >
              <button
                onClick={() => navigate(`/edit/${img.id}`)}
                className="w-full aspect-square"
              >
                <img
                  src={URL.createObjectURL(img.thumbnail)}
                  alt={img.name}
                  className="w-full h-full object-cover"
                />
              </button>

              {/* Overlay controls */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors pointer-events-none" />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <IconBtn onClick={() => toggleFavorite(img.id)} title="Favorite">
                  <Heart size={14} fill={img.favorite ? 'var(--sage-danger)' : 'none'} color={img.favorite ? 'var(--sage-danger)' : 'white'} />
                </IconBtn>
                <IconBtn onClick={() => handleDownload(img)} title="Download">
                  <Download size={14} />
                </IconBtn>
                <IconBtn onClick={() => setHistoryFor(img)} title="Version history">
                  <History size={14} />
                </IconBtn>
                <IconBtn onClick={() => { if (confirm('Delete this image?')) removeImage(img.id); }} title="Delete">
                  <Trash2 size={14} color="var(--sage-danger)" />
                </IconBtn>
              </div>

              {/* Info bar */}
              <div
                className="absolute bottom-0 left-0 right-0 px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
              >
                <p className="text-[10px] truncate" style={{ color: 'var(--sage-text)' }}>{img.name}</p>
                <p className="text-[9px]" style={{ color: 'var(--sage-text-muted)' }}>{img.width}x{img.height}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Backdrop */}
      {historyFor && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setHistoryFor(null)}
        />
      )}

      {/* Version history drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-80 z-50 shadow-2xl transition-transform ${historyFor ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          backgroundColor: 'var(--sage-sidebar)',
          borderLeft: '1px solid var(--sage-white-10)',
        }}
      >
        {historyFor && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: '1px solid var(--sage-white-10)' }}
            >
              <h2 className="text-sm font-semibold" style={{ fontFamily: 'var(--sage-font-serif)', color: 'var(--sage-text)' }}>
                Version History
              </h2>
              <button
                onClick={() => setHistoryFor(null)}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
                style={{ color: 'var(--sage-text-muted)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--sage-white-10)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
              {/* Subject */}
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Current
                </p>
                <div
                  className="flex items-center gap-3 p-2 rounded-lg"
                  style={{ backgroundColor: 'var(--sage-surface)', border: '1px solid var(--sage-white-10)' }}
                >
                  <img
                    src={URL.createObjectURL(historyFor.thumbnail)}
                    alt={historyFor.name}
                    className="w-14 h-14 rounded-md object-cover flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs truncate" style={{ color: 'var(--sage-text)' }}>{historyFor.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>
                      {historyFor.width}x{historyFor.height}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>
                      {new Date(historyFor.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Ancestors */}
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Ancestors
                </p>
                {ancestors.length === 0 ? (
                  <p className="text-xs italic" style={{ color: 'var(--sage-text-muted)' }}>
                    Original upload — no parents
                  </p>
                ) : (
                  <div className="space-y-2">
                    {ancestors.map(a => (
                      <HistoryRow
                        key={a.id}
                        image={a}
                        onOpen={() => setHistoryFor(a)}
                        onEdit={() => {
                          setHistoryFor(null);
                          navigate(`/edit/${a.id}`);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Children */}
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Derivatives
                </p>
                {children.length === 0 ? (
                  <p className="text-xs italic" style={{ color: 'var(--sage-text-muted)' }}>
                    No derivatives yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {children.map(c => (
                      <HistoryRow
                        key={c.id}
                        image={c}
                        onOpen={() => setHistoryFor(c)}
                        onEdit={() => {
                          setHistoryFor(null);
                          navigate(`/edit/${c.id}`);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function HistoryRow({ image, onOpen, onEdit }: { image: MedaImage; onOpen: () => void; onEdit: () => void }) {
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer"
      style={{ backgroundColor: 'var(--sage-surface)', border: '1px solid var(--sage-white-10)' }}
      onClick={onOpen}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--sage-white-10)')}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--sage-surface)')}
    >
      <img
        src={URL.createObjectURL(image.thumbnail)}
        alt={image.name}
        className="w-10 h-10 rounded-md object-cover flex-shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs truncate" style={{ color: 'var(--sage-text)' }}>{image.name}</p>
        <p className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>
          {image.operation || image.source}
        </p>
      </div>
      <button
        onClick={e => { e.stopPropagation(); onEdit(); }}
        title="Open editor"
        className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
        style={{ color: 'var(--sage-text-muted)' }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--sage-white-10)'; e.currentTarget.style.color = 'var(--sage-text)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--sage-text-muted)'; }}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function IconBtn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={title}
      className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white' }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
    >
      {children}
    </button>
  );
}
